import { computed, Injectable, signal } from '@angular/core';
import SendBird, { SendbirdChatWith, User } from '@sendbird/chat';
import { GroupChannel, GroupChannelHandler, GroupChannelModule, Member } from '@sendbird/chat/groupChannel';
import { RestrictedUser, SendableMessage, SendbirdError } from '@sendbird/chat/lib/__definition';
import {
  BaseMessage,
  MentionType,
  MessageListParams,
  MessageType,
  MessageTypeFilter,
  ReplyType,
  UserMessage,
  UserMessageCreateParams,
} from '@sendbird/chat/message';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { LoginService } from 'src/app/pages/login-dialog/login.service';
import { SendbirdChatApi } from 'src/app/shared/apis/sendbird-chat.api';
import { ToastService } from 'src/app/shared/service/toast.service';
import { environment } from 'src/environments/environment';
import { v4 as uuidv4 } from 'uuid';

@Injectable({ providedIn: 'root' })
export class SendbirdChatService {
  private appId = environment.sendbirdAPPId;
  private userId = '';
  private sb!: SendbirdChatWith<GroupChannelModule[]>;
  private uniqueChannelHandlerId = '';
  private uniqueConnectionHandlerId = '';
  private replyMessageInfo = signal<UserMessage | null>(null); // 用於回覆消息的父消息 ID
  private mutedUntil: number | null = null;
  private muteTimer: ReturnType<typeof setInterval> | null = null;
  private muteCountdown = signal<number>(0);
  // 閒置計時器相關
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly IDLE_TIMEOUT = 30 * 60 * 1000; // 30分鐘（毫秒）
  rawMessages = signal<{ message: BaseMessage; template: (string | { type: 'mention'; user: User })[] }[]>([]);
  blockUserList = signal<User[]>([]);
  displayChat$ = new BehaviorSubject<boolean>(false);
  userStatus$ = new BehaviorSubject<boolean>(false);
  channelList$ = new BehaviorSubject<{ value: string; label: string; disable: boolean }[]>([]);
  userList$ = new BehaviorSubject<Member[]>([]);
  selectedChannel = signal<GroupChannel | null>(null);
  isGuestMode = signal(true);
  restrictionType = signal<'mute' | 'ban' | 'deactivate' | 'idle' | 'full' | null>(null);
  readonly replyMessage = computed(() => this.replyMessageInfo());
  isLoading = signal<boolean>(true);
  isContactSupport = signal<boolean>(false);
  readonly remainingSeconds = computed(() => {
    const remaining = this.muteCountdown();
    if (!remaining) {
      return '';
    }

    const seconds = Math.floor((remaining % 60000) / 1000);
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutesInHour = Math.floor((remaining % 3600000) / 60000);

    if (days > 0) {
      return `${days}d ${this.pad(hours)}:${this.pad(minutesInHour)}:${this.pad(seconds)}`;
    }

    return `${this.pad(hours)}:${this.pad(minutesInHour)}:${this.pad(seconds)}`;
  });
  constructor(
    private sendbirdChatApi: SendbirdChatApi,
    private toast: ToastService,
    public loginService: LoginService,
  ) {}

  private pad(n: number): string {
    return n < 10 ? '0' + n : '' + n;
  }

  /*
   * 處理禁言或封鎖狀態更新
   */
  private async handleRestrictionUpdate(
    isRestricted: boolean,
    type: 'mute' | 'ban' | 'deactivate' | null,
    user: RestrictedUser | null,
  ) {
    if (isRestricted && user) {
      this.restrictionType.set(type);
      this.mutedUntil = user.restrictionInfo.endAt ?? null;
      if (this.mutedUntil !== null) {
        // 如果是ban 或 deactivate 需要清空聊天訊息並且清空channel list
        if (type === 'ban' || type === 'deactivate') {
          this.rawMessages.set([]);
          try {
            await this.selectedChannel()?.leave();
          } catch (error) {
            console.warn('leaving channel fail:', error);
          }
          this.channelList$.next([]);

          // 清除閒置計時器，因為已經被 ban 或 deactivate，不需要閒置計時器
          this.clearIdleTimer();
        }
        this.startMuteCountdown();
      }
    } else {
      this.mutedUntil = null;
      if (this.muteTimer) {
        clearInterval(this.muteTimer);
      }
      this.muteCountdown.set(0);
      this.restrictionType.set(null);
    }
  }

  private addChannelHandler() {
    const handler = new GroupChannelHandler({
      onMessageReceived: (ch, message) => {
        if (ch.url === this.selectedChannel()?.url && message.messageType === MessageType.USER) {
          const blockIdList = this.blockUserList().map(user => user.userId);

          if (!blockIdList.includes((message as SendableMessage)?.sender?.userId || '')) {
            this.rawMessages.update(prev => [...prev, this.parseMessage(message)]);
          }
        }
      },
      onUserJoined: (channel, user) => {
        // 確保只處理當前頻道的用戶加入事件
        if (channel.url !== this.selectedChannel()?.url) {
          return;
        }
        this.userStatus$.next(true);
        this.userList$.next([...this.userList$.value, user as Member]);
      },
      onUserLeft: (channel, user) => {
        // 確保只處理當前頻道的用戶加入事件
        if (channel.url !== this.selectedChannel()?.url) {
          return;
        }
        this.userStatus$.next(false);
        this.userList$.next(this.userList$.value.filter(u => u.userId !== user.userId));
      },
      onUserMuted: (channel, user) => {
        if (user.userId === this.userId) {
          this.handleRestrictionUpdate(true, 'mute', user);
        }
      },
      onUserUnmuted: (channel, user) => {
        if (user.userId === this.userId) {
          this.handleRestrictionUpdate(false, null, null);
        }
      },
      onUserBanned: async (channel, user) => {
        if (user.userId === this.userId) {
          await this.handleRestrictionUpdate(true, 'ban', user);
        }
      },
      onMentionReceived: (channel, message) => {
        if (message.mentionedUsers?.some((user: any) => user.userId === this.userId)) {
        }
      },
      onThreadInfoUpdated: (channel, threadInfoUpdateEvent) => {
        this.replyMessageInfo()?.applyThreadInfoUpdateEvent(threadInfoUpdateEvent);
        this.replyMessageInfo.set(null); // 清除回覆消息
      },
      onReactionUpdated: async (channel, reactionEvent) => {
        if (channel.url !== this.selectedChannel()?.url) {
          return;
        }

        this.rawMessages.update(prev =>
          prev.map(msg => {
            if (msg.message.messageId !== reactionEvent.messageId) {
              return msg;
            }
            const updated = { ...msg };
            updated.message.applyReactionEvent(reactionEvent);
            // 更新消息的反應事件
            return updated;
          }),
        );
      },
      onChannelMemberCountChanged: channel => {},
    });

    this.sb.groupChannel.removeGroupChannelHandler(this.uniqueChannelHandlerId);
    this.uniqueChannelHandlerId = uuidv4();
    this.sb.groupChannel.addGroupChannelHandler(this.uniqueChannelHandlerId, handler);

    return handler;
  }

  private async loadChannels() {
    try {
      const query = this.sb.groupChannel.createPublicGroupChannelListQuery({
        includeEmpty: true,
      });
      const list = await query.next();

      this.channelList$.next(
        list.map((item: any) => ({
          value: item.url,
          label: item.name || item.url,
          disable: item.type !== 'open',
          img: item.coverUrl || '',
        })),
      );

      const blockList = await this.sb.createBlockedUserListQuery().next();
      this.blockUserList.set(blockList);

      // add channel event handler
      this.addChannelHandler();
    } catch (error) {
      this.toast.show({
        message: 'Failed to load channels',
        type: 'fail',
      });
    }
  }

  private async getUserList(channel: GroupChannel) {
    const query = channel.createMemberListQuery({ limit: 100 }); // default is 20
    const members: Member[] = [];

    while (query.hasNext) {
      members.push(...(await query.next()));
    }

    return members;
  }

  private updateMuteCountdown(): void {
    if (!this.mutedUntil) {
      return;
    }

    const now = Date.now();
    const diff = this.mutedUntil - now;

    if (diff <= 0) {
      this.muteCountdown.set(0);
      this.mutedUntil = null;
      if (this.muteTimer) {
        clearInterval(this.muteTimer);
      }

      const wasBanned = this.restrictionType() === 'ban';

      // 先清除限制狀態
      this.restrictionType.set(null);

      if (wasBanned) {
        this.loadChannels();
        // ban 結束後，重新啟動閒置計時器
        this.resetIdleTimer();
      }

      return;
    }

    this.muteCountdown.set(diff);
  }

  private startMuteCountdown(): void {
    if (this.muteTimer) {
      clearInterval(this.muteTimer);
    }

    this.updateMuteCountdown();

    this.muteTimer = setInterval(() => {
      this.updateMuteCountdown();
    }, 1000);
  }

  private parseMessage(data: BaseMessage): {
    message: BaseMessage;
    template: (string | { type: 'mention'; user: User })[];
  } {
    const result: (string | { type: 'mention'; user: User })[] = [];
    let remaining = data.message;

    for (const user of data.mentionedUsers ?? []) {
      const mentionTag = `@${user.nickname}`;
      const index = remaining.indexOf(mentionTag);

      if (index === -1) continue;

      if (index > 0) {
        result.push(remaining.slice(0, index)); // 推入 mention 前的文字
      }

      result.push({ type: 'mention', user }); // 推入 mention 本體

      remaining = remaining.slice(index + mentionTag.length); // 去除已處理部分
    }

    if (remaining) {
      result.push(remaining.replace(/\n/g, '<br>')); // 剩餘文字
    }

    data.data = data.data ? JSON.parse(data.data) : null;

    return {
      message: data,
      template: result,
    };
  }

  async initSendbird(userId: string = '', token: string = '') {
    if (!userId) {
      this.toast.show({
        message: 'initialize chat failed, userId is required',
        type: 'fail',
      });
      return;
    }
    try {
      this.sb = await SendBird.init({
        appId: this.appId,
        modules: [new GroupChannelModule()],
      });
      let user: User | null = null;

      if (!token) {
        const response = await firstValueFrom(this.sendbirdChatApi.registerChat());

        if (response?.data) {
          const { userId, userName, accessToken, profile } = response.data;
          user = await this.sb.connect(userId, accessToken);
        }
      } else {
        this.userId = userId;
        user = await this.sb.connect(this.userId, token);
      }

      if (!user) {
        this.toast.show({
          message: 'initialize chat failed, user is null',
          type: 'fail',
        });
        return;
      }

      // 更新用戶暱稱
      // await this.sb.updateCurrentUserInfo({
      //   nickname: userId,
      // });

      // 清除閒置狀態和人滿狀態（如果有的話）
      if (this.restrictionType() === 'idle' || this.restrictionType() === 'full') {
        this.restrictionType.set(null);
      }

      // 啟動閒置計時器
      this.resetIdleTimer();

      this.loadChannels();
    } catch (error) {
      const { code, message } = error as SendbirdError;
      switch (code) {
        // user deactivated
        case 400300:
          this.toast.show({
            message: message || '',
            type: 'fail',
          });
          this.isContactSupport.set(true);
          this.handleRestrictionUpdate(true, 'deactivate', {
            restrictionInfo: { endAt: 0 },
          } as RestrictedUser);

          this.isLoading.set(false);
          break;
        default:
          this.toast.show({
            message: 'Chat init/connect failed:',
            type: 'fail',
          });
          break;
      }

      // 初始化失敗，清理 sb 實例
      this.sb = null as any;
    }
  }

  async joinChannel(channelUrl: string) {
    this.isLoading.set(true);
    try {
      // 清除之前的訊息
      this.rawMessages.set([]);
      // 離開當前頻道
      await this.selectedChannel()?.leave();

      const currentChannel = await this.sb.groupChannel.getChannel(channelUrl);

      await currentChannel.join();
      this.selectedChannel.set(currentChannel);

      // 清除人滿狀態（如果有的話）
      if (this.restrictionType() === 'full') {
        this.restrictionType.set(null);
      }

      // 取得當前聊天室成員名單
      const list = await this.getUserList(currentChannel);
      this.userList$.next(list);

      // 確認是否有被禁言
      currentChannel.getMyMutedInfo().then(({ isMuted, endAt }) => {
        if (isMuted) {
          this.handleRestrictionUpdate(true, 'mute', {
            restrictionInfo: { endAt },
          } as RestrictedUser);
        }
      });

      const params: MessageListParams = {
        prevResultSize: 100,
        nextResultSize: 0,
        isInclusive: false,
        includeReactions: true,
        includeParentMessageInfo: true,
        replyType: ReplyType.ALL, // 包含所有回覆類型
        messageTypeFilter: MessageTypeFilter.USER, // 僅獲取用戶消息
      };

      // 取得聊天室歷史訊息
      const messageList = (await currentChannel.getMessagesByTimestamp(Date.now(), params)) as SendableMessage[];
      const blockIdList = this.blockUserList().map(user => user.userId);
      this.rawMessages.set(
        messageList
          .filter(item => !blockIdList.includes(item.sender.userId))
          .map((message: BaseMessage) => {
            return this.parseMessage(message);
          }),
      );
    } catch (error) {
      const { code, message } = error as SendbirdError;
      switch (code) {
        // channel capacity exceeded
        case 400500:
          this.restrictionType.set('full');
          this.toast.show({
            message: 'The chat room is full. Please try again later.',
            type: 'fail',
          });
          break;
        // user banned
        case 400750:
          this.toast.show({
            message: message || '',
            type: 'fail',
          });
          this.isContactSupport.set(true);
          this.handleRestrictionUpdate(true, 'ban', {
            restrictionInfo: { endAt: 0 },
          } as RestrictedUser);
          break;
        default:
          this.toast.show({
            message: 'Failed to join channel',
            type: 'fail',
          });
          break;
      }
    }
    this.isLoading.set(false);
  }

  async sendMessage(msg: string, mentionList: string[] = []) {
    const channel = this.selectedChannel();
    if (!msg.trim() || !channel) {
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        const params: UserMessageCreateParams = {
          message: msg,

          // 使用 sendbird 的回覆功能
          parentMessageId: this.replyMessageInfo() ? this.replyMessageInfo()?.messageId : undefined,
          // 使用自訂義的回覆功能
          // data: this.replyMessageInfo()
          //   ? JSON.stringify({
          //       type: 'reply',
          //       content: this.replyMessageInfo(),
          //     })
          //   : undefined,
        };

        if (mentionList.length > 0) {
          params.mentionType = MentionType.USERS;
          params.mentionedUserIds = mentionList;
        }

        channel
          .sendUserMessage(params)
          .onSucceeded((message: any) => {
            this.rawMessages.update(prev => [...prev, this.parseMessage(message)]);
            resolve(undefined);
          })
          .onFailed((error: any) => {
            const { code, message } = error as SendbirdError;
            this.toast.show({
              message: message || '',
              type: 'fail',
            });

            if (code === 900021) {
              this.handleRestrictionUpdate(true, 'deactivate', {
                restrictionInfo: { endAt: 0 },
              } as RestrictedUser);
            }

            reject(error);
          });
      } catch (error) {
        this.toast.show({
          message: 'Send failed',
          type: 'fail',
        });
      }
    });
  }

  toggleChat() {
    this.displayChat$.next(!this.displayChat$.value);
  }
  openChat() {
    this.displayChat$.next(true);
  }

  hideChat() {
    this.displayChat$.next(false);
  }

  setReplyMessage(message: UserMessage | null) {
    this.replyMessageInfo.set(message);
  }

  async addLike(message: UserMessage) {
    return await this.selectedChannel()?.addReaction(message, 'like');
  }
  async removeLike(message: UserMessage) {
    return await this.selectedChannel()?.deleteReaction(message, 'like');
  }

  async blockUser(user: User) {
    try {
      await this.sb.blockUser(user.userId);
      this.toast.show({ message: 'The user has been silenced', type: 'success' });
      this.blockUserList.update(prev => [...prev, user]);
    } catch (error) {
      this.toast.show({ message: 'Failed to silence the user', type: 'fail' });
    }
  }

  async unblockUser(user: User) {
    try {
      await this.sb.unblockUser(user.userId);
      this.toast.show({ message: 'The user has been unsilenced', type: 'success' });
      this.blockUserList.update(prev => prev.filter(u => u.userId !== user.userId));
    } catch (error) {
      this.toast.show({ message: 'Failed to unsilenced the user', type: 'fail' });
    }
  }

  checkBlockedUser(userId: string): boolean {
    return this.blockUserList().some(user => user.userId === userId);
  }

  checkSelfUser(userId: string): boolean {
    return this.userId === userId;
  }

  async updateUserAvatar(profileUrl: string) {
    try {
      await this.sb.updateCurrentUserInfo({
        profileUrl,
      });

      this.toast.show({ message: 'Profile updated successfully', type: 'success' });
    } catch (error) {
      this.toast.show({ message: 'Profile updated fail', type: 'fail' });
    }
  }
  async updateNickname(nickname: string) {
    try {
      await this.sb.updateCurrentUserInfo({
        nickname,
      });

      this.toast.show({ message: 'Nickname updated successfully', type: 'success' });
    } catch (error) {
      this.toast.show({ message: 'Nickname updated fail', type: 'fail' });
    }
  }

  /**
   * 重置閒置計時器
   * 當用戶有任何互動行為時調用此方法
   */
  resetIdleTimer() {
    // 清除現有的計時器
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }

    // 如果用戶已被 ban 或 deactivate，不啟動計時器
    const currentRestriction = this.restrictionType();
    if (currentRestriction === 'ban' || currentRestriction === 'deactivate') {
      return;
    }

    // 只有在已連線且已初始化的情況下才啟動新的計時器
    if (this.isInitialized() && this.sb?.connectionState === 'OPEN') {
      this.idleTimer = setTimeout(() => {
        this.disconnectDueToIdle();
      }, this.IDLE_TIMEOUT);
    }
  }

  /**
   * 因閒置而斷開連線
   */
  private async disconnectDueToIdle() {
    try {
      // 清除閒置計時器
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
        this.idleTimer = null;
      }

      // 設置閒置狀態，顯示 UI
      this.restrictionType.set('idle');

      // 斷開 Sendbird 連線
      if (this.sb) {
        await this.sb.disconnect();
      }

      // 清空所有相關數據
      this.sb = null as any;
      this.userId = '';
      this.rawMessages.set([]);
      this.blockUserList.set([]);
      this.channelList$.next([]);
      this.userList$.next([]);
      this.selectedChannel.set(null);
      this.replyMessageInfo.set(null);

      // 清除禁言相關狀態
      if (this.muteTimer) {
        clearInterval(this.muteTimer);
        this.muteTimer = null;
      }
      this.mutedUntil = null;
      this.muteCountdown.set(0);
    } catch (error) {
      console.error('[Sendbird] Error disconnecting:', error);
    }
  }

  /**
   * 清除閒置計時器
   * 當用戶登出或關閉聊天時調用
   */
  clearIdleTimer() {
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  setGuestMode(isGuest: boolean) {
    this.isGuestMode.set(isGuest);
  }

  isInitialized(): boolean {
    return !!this.sb;
  }

  /**
   * 檢查是否需要登入，如果是訪客模式，任何互動都需要redirect到登入頁
   *
   * @returns
   */
  checkNeedToLogin(): boolean {
    if (this.isGuestMode()) {
      // redirect to login page
      this.loginService.openLogin();
      return true;
    }

    return false;
  }

  handleSignalMsg(userId: string, type: 'ban' | 'deactivate') {
    if (userId !== this.userId) {
      return;
    }

    switch (type) {
      case 'ban':
        this.handleRestrictionUpdate(false, null, null);
        this.loadChannels();
        break;
      case 'deactivate':
        this.handleRestrictionUpdate(false, null, null);
        this.loadChannels();
        break;
      default:
        break;
    }
  }
}
